import dayjs from 'dayjs'
import { CurrencyFormatter } from 'helpers/formatters'
import { isReturnableSource } from '../ReconciliationV2/AdjustPaymentModal.constants'
import type {
  AllocationTicket,
  TransactionViewAbsorption,
  TransactionViewAllocation,
  TransactionViewRow,
} from '../../api/transaction-details-api-client'

/**
 * Pure/shared helpers for the TransactionDetails subtree — money/date/month
 * formatting, source→label mapping, split-row flattening and search-term
 * highlighting. No React/JSX here (kept a plain .ts module per spec); the
 * table row component turns `getHighlightSegments` output into markup.
 */

const currencyFormatter = new CurrencyFormatter()

export const DASH = '—'

/**
 * Middle-truncate a long identifier for display, e.g. a benefit id renders as
 * `1234...7890` (first `head` + last `tail` chars). Keep the full value in a
 * `title` tooltip on the element. Values short enough to fit pass through
 * unchanged.
 */
export const truncateMiddle = (
  value: string | null | undefined,
  head = 4,
  tail = 4
): string => {
  const s = value ?? ''
  if (s.length <= head + tail + 3) return s
  return `${s.slice(0, head)}...${s.slice(s.length - tail)}`
}

export const formatMoney = (
  value: number | string | null | undefined
): string => currencyFormatter.safeFormat(value ?? undefined, DASH)

export const formatDate = (value: string | null | undefined): string =>
  value ? dayjs(value).format('MM/DD/YYYY') : DASH

/** Coverage month → bare month name (design shows no year on this column). */
export const formatMonth = (value: string | null | undefined): string =>
  value ? dayjs(value).format('MMMM') : DASH

export const SOURCE_LABELS: Record<string, string> = {
  marqeta: 'Marqeta',
  wex: 'WEX',
  i_stream: 'iStream',
  check: 'Checks',
  group_ach: 'Group ACH',
  ee_paid: 'EE Paid',
}

export const sourceLabel = (source: string | null | undefined): string =>
  (source && SOURCE_LABELS[source]) || source || DASH

/**
 * The payment-type filter options from the design mock (plus EE Paid, added
 * after Group ACH), in display order. Each maps 1:1 to a backend `source`
 * enum value; the filter bar's multiselect sends the selected `source`s
 * joined as the `sources` param.
 */
export const PAYMENT_TYPE_OPTIONS: { label: string; source: string }[] = [
  { label: 'Marqeta', source: 'marqeta' },
  { label: 'WEX', source: 'wex' },
  { label: 'iStream', source: 'i_stream' },
  { label: 'Checks', source: 'check' },
  { label: 'Group ACH', source: 'group_ach' },
  { label: 'EE Paid', source: 'ee_paid' },
]

export const ALL_PAYMENT_SOURCES = PAYMENT_TYPE_OPTIONS.map((o) => o.source)

/** Recon-status filter options — maps to the `reconStatus` query param. */
export const RECON_STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'Reconciled', value: 'reconciled' },
  { label: 'Pending', value: 'pending' },
  { label: 'Refund', value: 'refund' },
  { label: 'Absorbed', value: 'absorbed' },
  { label: 'Returned', value: 'returned' },
]

export const ALL_RECON_STATUSES = RECON_STATUS_OPTIONS.map((o) => o.value)

/**
 * Note-type filter options — maps to the `noteTypes` query param. Unlike
 * recon status, selecting all of them still filters: the transaction must
 * carry a non-blank note of some type.
 */
export const NOTE_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Absorption', value: 'absorption' },
  { label: 'Return', value: 'return' },
  { label: 'Allocation', value: 'allocation' },
]

export const ALL_NOTE_TYPES = NOTE_TYPE_OPTIONS.map((o) => o.value)

/**
 * Note column text for a row: the note typed in the Adjust Payment "Allocate"
 * step. A plain/child row shows its allocation's note; a split parent row
 * summarizes across its allocations (single value, "Multiple", or dash).
 */
export const allocationNote = (
  txn: TransactionViewRow,
  allocation: TransactionViewAllocation | null,
  isParent: boolean
): string =>
  isParent
    ? summarizeAllocationField(txn.allocations, (a) => a.note)
    : (allocation?.note ?? DASH)

/**
 * Note column text for a transaction's own row: its allocation note, or, on a
 * returned transaction, the operator's return note. A return applies only to
 * an unallocated tx, so the two never compete. Absorption sub-rows show their
 * own note and don't go through here.
 */
export const rowNote = (
  txn: TransactionViewRow,
  allocation: TransactionViewAllocation | null,
  isParent: boolean
): string => {
  const note = allocationNote(txn, allocation, isParent)
  return note === DASH && txn.txReturn ? txn.txReturn.note : note
}

export enum ReconStatusLabel {
  Refund = 'Refund',
  Reconciled = 'Reconciled',
  Pending = 'Pending',
  Absorbed = 'Absorbed',
  Returned = 'Returned',
}

/**
 * Recon status for a transaction: an operator-returned tx is always "Returned"
 * (returns apply only to unallocated, un-absorbed txs); a refund is "Refund"; otherwise
 * "Reconciled" when it has at least one allocation; a FULLY written-off tx (no
 * allocation and absorptions covering the whole amount) reads "Absorbed";
 * "Pending" otherwise — including a tx that is only partially absorbed but
 * unallocated (it still has an unaccounted remainder). Mirrors the backend
 * derivation so the label, the Absorbed stat/filter, and the queue agree.
 * Shared by the Recon column (TransactionRow) and the detail modal.
 */
export const reconStatusLabel = (
  txn: Pick<
    TransactionViewRow,
    'transactionType' | 'allocations' | 'absorbedAmount' | 'amount' | 'txReturn'
  >
): ReconStatusLabel => {
  if (txn.txReturn) return ReconStatusLabel.Returned
  if (txn.transactionType === 'refund') return ReconStatusLabel.Refund
  if (txn.allocations.length >= 1) return ReconStatusLabel.Reconciled
  // Fully absorbed = write-off covers the whole amount (half-cent epsilon guards
  // dollar-rounding). A partial absorb on an unallocated tx stays Pending.
  if (
    txn.absorbedAmount > 0 &&
    txn.absorbedAmount + 0.005 >= Math.abs(txn.amount)
  ) {
    return ReconStatusLabel.Absorbed
  }
  return ReconStatusLabel.Pending
}

export const insuredPersonName = (
  source: Pick<TransactionViewAllocation, 'insuredPersons' | 'employeeName'>
): string => {
  const person = source.insuredPersons?.[0]
  if (person) return `${person.firstName} ${person.lastName}`.trim() || DASH
  return source.employeeName || DASH
}

/** Single value when every allocation agrees, "Multiple" when they don't, dash when none. */
export const summarizeAllocationField = (
  allocations: TransactionViewAllocation[],
  pick: (a: TransactionViewAllocation) => string | null | undefined
): string => {
  const values = Array.from(
    new Set(allocations.map(pick).filter((v): v is string => Boolean(v)))
  )
  if (values.length === 0) return DASH
  if (values.length === 1) return values[0]
  return 'Multiple'
}

/**
 * Flattens the Zendesk tickets across every allocation of a transaction
 * (used for the split-parent row and the detail modal, both of which need an
 * aggregate view across allocations), deduping by `ticketUrl` and keeping the
 * first occurrence's order — allocations are already latest-first per the
 * backend contract, so this preserves that ordering across the whole set.
 */
export const collectAllocationTickets = (
  allocations: TransactionViewAllocation[]
): AllocationTicket[] => {
  const seen = new Set<string>()
  const out: AllocationTicket[] = []
  for (const allocation of allocations) {
    for (const ticket of allocation.tickets ?? []) {
      if (seen.has(ticket.ticketUrl)) continue
      seen.add(ticket.ticketUrl)
      out.push(ticket)
    }
  }
  return out
}

// ── split-row flattening ─────────────────────────────────────────────────────

export type DisplayRowKind = 'plain' | 'parent' | 'child' | 'absorption'

export interface DisplayRow {
  key: string
  kind: DisplayRowKind
  txn: TransactionViewRow
  /** The one allocation this row represents; null for a 0-allocation (Pending) row, a split parent, or an absorption row. */
  allocation: TransactionViewAllocation | null
  /** The absorption this row represents; set only for `kind: 'absorption'`. */
  absorption?: TransactionViewAbsorption
  childIndex?: number
  childCount?: number
}

/**
 * Flattens each transaction into one or more display rows: a plain row when
 * there are 0 or 1 allocations, or a "Split N" parent row followed by one
 * indented child row per allocation when there are more than 1.
 */
export const buildDisplayRows = (rows: TransactionViewRow[]): DisplayRow[] => {
  const out: DisplayRow[] = []
  rows.forEach((txn) => {
    if (txn.allocations.length > 1) {
      out.push({
        key: txn.transactionId,
        kind: 'parent',
        txn,
        allocation: null,
      })
      txn.allocations.forEach((allocation, i) => {
        out.push({
          key: `${txn.transactionId}-${i}`,
          kind: 'child',
          txn,
          allocation,
          childIndex: i + 1,
          childCount: txn.allocations.length,
        })
      })
    } else {
      out.push({
        key: txn.transactionId,
        kind: 'plain',
        txn,
        allocation: txn.allocations[0] ?? null,
      })
    }
    // Absorptions render as indented sub-rows beneath the transaction's
    // allocation rows — like split children, but money we absorbed rather than
    // allocated.
    txn.absorptions.forEach((absorption) => {
      out.push({
        key: `${txn.transactionId}-absorption-${absorption.id}`,
        kind: 'absorption',
        txn,
        allocation: null,
        absorption,
      })
    })
  })
  return out
}

/**
 * Per-row action button label. Allocated rows (a split parent, a child, or a
 * plain row that has an allocation) → "Edit". Unallocated rows → "Allocate",
 * or "Post" for a refund — mirroring the Required Action tab's wording
 * (REASON_ACTION_LABEL in ReviewQueue).
 */
/**
 * Whether the row can be returned: a plain (tx-level) iStream row with no
 * allocation and no absorption, not already returned — mirrors the EPS
 * returnTransaction guards.
 */
export const canReturnRow = (row: DisplayRow): boolean =>
  row.kind === 'plain' &&
  isReturnableSource(row.txn.source) &&
  !row.txn.txReturn &&
  row.txn.allocations.length === 0 &&
  row.txn.absorptions.length === 0

export const rowActionLabel = (
  row: DisplayRow
): 'Allocate' | 'Post' | 'Edit' => {
  const allocated = row.kind === 'parent' || row.allocation != null
  if (allocated) return 'Edit'
  return row.txn.transactionType === 'refund' ? 'Post' : 'Allocate'
}

// ── search highlighting ──────────────────────────────────────────────────────

export interface HighlightSegment {
  text: string
  matched: boolean
}

/** Splits `text` into segments so callers can render the first case-insensitive match of `query` highlighted. */
export const getHighlightSegments = (
  text: string,
  query: string
): HighlightSegment[] => {
  const q = query.trim()
  if (!q || !text) return [{ text, matched: false }]
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx === -1) return [{ text, matched: false }]
  const segments: HighlightSegment[] = []
  if (idx > 0) segments.push({ text: text.slice(0, idx), matched: false })
  segments.push({ text: text.slice(idx, idx + q.length), matched: true })
  const rest = text.slice(idx + q.length)
  if (rest) segments.push({ text: rest, matched: false })
  return segments
}

// ── table layout ─────────────────────────────────────────────────────────────

/** 15 columns: Carrier, Benefit ID, Company EE ID, Company Name, Insured Person
 *  Name, Transaction ID, Payment Type, Month, Amount, Date, Recon, Note, Ticket,
 *  Action (allocate/edit button), Log (per-tx event-history action). */
// Column order: the sortable original-table columns first (Transaction Number,
// Transaction ID, Payment Type, Amount, Date), then the benefit/allocation
// columns, then the Recon/Note/Ticket and the Action/Log action columns. Header
// (TableHead), body (TransactionRow — both the normal and absorption renders)
// and this template must stay in the SAME order.
export const GRID_TEMPLATE_COLUMNS =
  'minmax(180px,0.85fr) minmax(130px,0.6fr) minmax(122px,0.5fr) minmax(110px,0.5fr) minmax(112px,0.5fr) minmax(150px,0.7fr) minmax(120px,0.55fr) minmax(134px,0.55fr) minmax(190px,1fr) minmax(150px,0.8fr) minmax(96px,0.45fr) minmax(104px,0.5fr) minmax(180px,0.85fr) minmax(140px,0.7fr) minmax(156px,0.6fr) minmax(72px,0.35fr)'

// Action column fits two buttons (Allocate + Return).
export const GRID_MIN_WIDTH = 2244

/** Matches BenefitsTable's `pageSizeOptions` (recon v2) — 200 dropped for consistency. */
export const PAGE_SIZE_OPTIONS = [25, 50, 100]

// ── sorting ──────────────────────────────────────────────────────────────────

/**
 * Sortable column keys, matching the backend's `sortBy` enum 1:1. Restricted to
 * columns on the original all_payment_transaction table — the benefit/allocation-
 * derived columns (carrier, benefit id, company, insured, month, recon/note/
 * ticket) are intentionally NOT sortable, since sorting them required joins.
 */
export type SortColumnKey =
  | 'transactionNumber'
  | 'transactionId'
  | 'paymentType'
  | 'amount'
  | 'date'

export const SORT_COLUMN_KEYS: SortColumnKey[] = [
  'transactionNumber',
  'transactionId',
  'paymentType',
  'amount',
  'date',
]

export type SortOrder = 'asc' | 'desc'

export const isSortColumnKey = (value: string): value is SortColumnKey =>
  (SORT_COLUMN_KEYS as string[]).includes(value)

// ── CSV export ───────────────────────────────────────────────────────────────

const csvEscape = (value: string | number | null | undefined): string =>
  `"${String(value ?? '').replace(/"/g, '""')}"`

/** Builds a CSV of the currently-loaded page (server pagination means a
 *  full-dataset export would need to walk every page — deferred to v2). */
export const buildTransactionsCsv = (rows: DisplayRow[]): string => {
  const head = [
    'Transaction Number',
    'Transaction ID',
    'Payment Type',
    'Amount',
    'Date',
    'Carrier',
    'Benefit ID',
    'Company EE ID',
    'Company Name',
    'Insured Person Name',
    'Month',
    'Recon',
    'Note',
  ]
  const lines = [head.map(csvEscape).join(',')]
  rows.forEach(({ kind, txn, allocation, absorption }) => {
    // Absorption sub-row: a company write-off, not an allocation. Emit its own
    // negative-amount line (mirroring the ledger) so the export never repeats
    // the parent transaction amount, which would double-count the money.
    if (kind === 'absorption' && absorption) {
      lines.push(
        [
          '',
          '',
          'Absorbed',
          formatMoney(-absorption.amount),
          formatDate(absorption.createdAt),
          DASH,
          DASH,
          DASH,
          DASH,
          `Absorbed · ${absorption.absorptionType}`,
          DASH,
          'Absorbed',
          // Note column: the write-off justification.
          absorption.note ?? DASH,
        ]
          .map(csvEscape)
          .join(',')
      )
      return
    }
    const isParent = kind === 'parent'
    // Match the ledger table: unallocated plain rows fall back to resolvedBenefit
    // for carrier / benefit / company / insured (month stays dash until allocated).
    const resolved = allocation ? null : txn.resolvedBenefit
    const carrier = isParent
      ? summarizeAllocationField(txn.allocations, (a) => a.carrierName)
      : kind === 'child'
        ? `> ${allocation?.carrierName ?? DASH}`
        : (allocation?.carrierName ?? resolved?.carrierName ?? DASH)
    const benefitId = isParent
      ? DASH
      : (allocation?.benefitId ?? resolved?.benefitId ?? DASH)
    const eeId = isParent
      ? DASH
      : (allocation?.employeePublicKey ?? resolved?.employeePublicKey ?? DASH)
    const companyName = isParent
      ? summarizeAllocationField(txn.allocations, (a) => a.companyName)
      : (allocation?.companyName ?? resolved?.companyName ?? DASH)
    const insured = isParent
      ? `Split ${txn.allocations.length}`
      : allocation
        ? insuredPersonName(allocation)
        : resolved
          ? insuredPersonName(resolved)
          : DASH
    const month = allocation ? formatMonth(allocation.periodStartDate) : DASH
    const amount =
      kind === 'child' && allocation
        ? formatMoney(Number(allocation.amount))
        : formatMoney(txn.amount)
    lines.push(
      [
        kind === 'child' ? '' : (txn.transactionNumber ?? DASH),
        kind === 'child' ? '' : txn.transactionId,
        sourceLabel(txn.source),
        amount,
        formatDate(txn.transactionDate),
        carrier,
        benefitId,
        eeId,
        companyName,
        insured,
        month,
        reconStatusLabel(txn),
        rowNote(txn, allocation, isParent),
      ]
        .map(csvEscape)
        .join(',')
    )
  })
  return lines.join('\n')
}

export const downloadCsv = (filename: string, csv: string): void => {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
